import React, { useState } from "react";
import { client } from "../../../api/client";
import { extractApiError } from "../helpers";
import { AdminUser } from "../types";

interface Props {
  target: AdminUser | null;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

const ResetPasswordModal: React.FC<Props> = ({ target, onClose, onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!target) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await client.post(`/auth/admin/users/${target.id}/reset-password/`, {
        new_password: password,
      });
      onSuccess(target.email);
      setPassword("");
    } catch (err) {
      setError(extractApiError(err, "Reset failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-xl">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Reset password for {target.email}
        </h3>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {error && (
            <div
              className="p-3 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}
          <div>
            <label
              htmlFor="reset-pw"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Temporary password
            </label>
            <input
              id="reset-pw"
              name="reset-pw"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full p-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-gray-500">
              Min 8 characters. Share with the user securely; they can change it
              themselves afterwards.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? "Resetting..." : "Reset"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
